<template>
  <div class="recognize-container">
    <div class="recognize-card">
      <div class="card-header">
        <h1>
          <i class="el-icon-video-camera"></i>
          Video Recognition & Analysis
        </h1>
        <p class="subtitle">Upload videos and audio for intelligent content analysis</p>
      </div>

      <div class="upload-section">
        <!-- Video Upload -->
        <div class="upload-box">
          <div class="upload-header">
            <i class="el-icon-video-camera-solid"></i>
            <h3>Video Files</h3>
            <span class="file-count" v-if="videoFiles.length > 0">{{ videoFiles.length }} file(s)</span>
          </div>
          <el-upload ref="videoUpload" class="upload-area" drag multiple accept="video/*"
            :auto-upload="false" :on-change="handleVideoChange" :on-remove="handleVideoRemove"
            :file-list="videoFiles"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              Drop video files here or <em>click to upload</em>
            </div>
            <div class="el-upload__tip" slot="tip">
              You can upload multiple video files (MP4)
            </div>
          </el-upload>
        </div>

        <!-- Audio Upload -->
        <div class="upload-box">
          <div class="upload-header">
            <i class="el-icon-headset"></i>
            <h3>Audio Track</h3>
            <span class="file-count" v-if="audioFile">1 file</span>
          </div>
          <el-upload ref="audioUpload" class="upload-area" drag accept="audio/*"
            :auto-upload="false" :on-change="handleAudioChange" :on-remove="handleAudioRemove" 
            :file-list="audioFile ? [audioFile] : []" :limit="1" 
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">
              Drop audio file here or <em>click to upload</em>
            </div>
            <div class="el-upload__tip" slot="tip">
              Upload one audio file (MP3)
            </div>
          </el-upload>
        </div>
      </div>

      <!-- Vocals Checkbox -->
      <div class="vocals-section">
        <el-checkbox :value="hasVocals" @input="setHasVocals" size="large">
          <span class="checkbox-label">
            <i class="el-icon-microphone"></i>
            Audio track contains vocals
          </span>
        </el-checkbox>
      </div>

      <!-- Tier Selection Section -->
      <div class="tier-section">
        <div class="tier-selection-box">
          <div class="tier-label">
            <i class="el-icon-s-grid"></i>
            <span>Analysis Tier</span>
          </div>
          <div class="tier-display">
            <el-tag
              v-if="selectedTier"
              :type="selectedTier === 1 ? 'primary' : selectedTier === 2 ? 'warning' : 'success'"
              size="large"
              class="tier-tag"
            >
              Tier {{ selectedTier }}: {{ tierName }}
            </el-tag>
            <span v-else class="no-tier-selected">No tier selected</span>
          </div>
          <el-button
            type="primary"
            size="medium"
            @click="openTierModal"
            class="select-tier-button"
          >
            <i class="el-icon-setting"></i>
            {{ selectedTier ? 'Change Tier' : 'Select Tier' }}
          </el-button>
        </div>
      </div>

      <!-- Uploaded Files Summary -->
      <div v-if="videoFiles.length > 0 || audioFile" class="files-summary">
        <h3>
          <i class="el-icon-document"></i>
          Uploaded Files Summary
        </h3>
        <div class="summary-content">
          <div v-if="videoFiles.length > 0" class="summary-item">
            <div class="summary-label">
              <i class="el-icon-video-camera-solid"></i>
              Videos
            </div>
            <div class="summary-files">
              <div v-for="(file, index) in videoFiles" :key="'video-' + index" class="file-tag">
                <i class="el-icon-document"></i>
                {{ file.name }}
                <span class="file-size">{{ formatFileSize(file.size) }}</span>
              </div>
            </div>
          </div>
          <div v-if="audioFile" class="summary-item">
            <div class="summary-label">
              <i class="el-icon-headset"></i>
              Audio
            </div>
            <div class="summary-files">
              <div class="file-tag">
                <i class="el-icon-document"></i>
                {{ audioFile.name }}
                <span class="file-size">{{ formatFileSize(audioFile.size) }}</span>
              </div>
            </div>
          </div>
          <div class="summary-item">
            <div class="summary-label">
              <i class="el-icon-microphone"></i>
              Vocals
            </div>
            <div class="summary-value">
              <el-tag :type="hasVocals ? 'success' : 'info'">
                {{ hasVocals ? 'Yes' : 'No' }}
              </el-tag>
            </div>
          </div>
          <div v-if="selectedTier" class="summary-item">
            <div class="summary-label">
              <i class="el-icon-s-grid"></i>
              Analysis Tier
            </div>
            <div class="summary-value">
              <el-tag :type="selectedTier === 1 ? 'primary' : selectedTier === 2 ? 'warning' : 'success'">
                Tier {{ selectedTier }}: {{ tierName }}
              </el-tag>
            </div>
          </div>
        </div>
      </div>

      <!-- Analysis Status -->
      <div v-if="analysisStatus" class="analysis-status">
        <div v-if="analysisStatus === 'uploading'" class="status-box uploading">
          <i class="el-icon-loading"></i>
          <h3>Uploading files...</h3>
          <p>Please wait while we upload your files</p>
        </div>
        <div v-else-if="analysisStatus === 'analyzing'" class="status-box analyzing">
          <i class="el-icon-loading"></i>
          <h3>Analyzing content...</h3>
          <p>Job ID: <span class="job-id">{{ jobId }}</span></p>
          <p class="status-note">This may take a few minutes. We're checking every 5 seconds.</p>
        </div>
        <div v-else-if="analysisStatus === 'completed'" class="status-box completed">
          <i class="el-icon-success"></i>
          <h3>Analysis Completed!</h3>
          <p>Your content has been successfully analyzed</p>
          <el-button
            v-if="analysisResults"
            type="success"
            size="medium"
            @click="openResultsModal"
            class="view-results-button"
          >
            <i class="el-icon-data-analysis"></i>
            View Results
          </el-button>
        </div>
        <div v-else-if="analysisStatus === 'error'" class="status-box error">
          <i class="el-icon-error"></i>
          <h3>Analysis Failed</h3>
          <p>{{ errorMessage }}</p>
        </div>
      </div>

      <!-- Action Button -->
      <div class="action-section">
        <el-button type="primary" size="large" class="submit-button" @click="startAnalysis"
          :disabled="!canSubmit || analysisStatus === 'analyzing' || analysisStatus === 'uploading'"
          :loading="analysisStatus === 'uploading' || analysisStatus === 'analyzing'"
        >
          <i class="el-icon-video-play"></i>
          {{ buttonText }}
        </el-button>
        <el-button v-if="analysisStatus" size="large" @click="handleResetForm" class="reset-button">
          <i class="el-icon-refresh"></i>
          Start New Analysis
        </el-button>
      </div>
    </div>

    <!-- Tier Selection Modal -->
    <tier-selection-modal
      :visible.sync="tierModalVisible"
      :current-tier="selectedTier"
      @tier-selected="handleTierSelected"
    />

    <!-- Analysis Results Modal -->
    <analysis-results-modal
      :visible.sync="resultsModalVisible"
      :results="analysisResults"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import TierSelectionModal from '../components/TierSelectionModal.vue'
import AnalysisResultsModal from '../components/AnalysisResultsModal.vue'

export default {
  name: 'Recognize',

  components: {
    TierSelectionModal,
    AnalysisResultsModal
  },

  data: function() {
    return {
      tierModalVisible: false,
      resultsModalVisible: false
    }
  },

  computed: {
    ...mapState('analyze', ['videoFiles', 'audioFile', 'hasVocals', 'selectedTier', 'analysisStatus', 'jobId', 'errorMessage', 'analysisResults']),
    ...mapGetters('analyze', ['canSubmit', 'buttonText']),

    tierName: function() {
      var tierNames = {
        1: 'Core Metadata Pack',
        2: 'Advanced Audio Report',
        3: 'Complete 360 Report'
      }
      return tierNames[this.selectedTier] || 'Not Selected'
    }
  },

  methods: {
    ...mapMutations('analyze', ['setVideoFiles', 'setAudioFile', 'setHasVocals', 'setSelectedTier']),
    ...mapActions('analyze', ['startAnalysis', 'stopPolling', 'resetForm']),

    openTierModal: function() {
      this.tierModalVisible = true
    },

    handleTierSelected: function(tier) {
      this.setSelectedTier(tier)
    },

    openResultsModal: function() {
      this.resultsModalVisible = true
    },

    handleVideoChange(_file, fileList) {
      this.setVideoFiles(fileList)
    },

    handleVideoRemove(_file, fileList) {
      this.setVideoFiles(fileList)
    },

    handleAudioChange(file) {
      this.setAudioFile(file)
    },

    handleAudioRemove(_file) {
      this.setAudioFile(null)
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'

      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']

      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    handleResetForm() {
      this.resetForm()

      if (this.$refs.videoUpload) this.$refs.videoUpload.clearFiles();
    
      if (this.$refs.audioUpload) this.$refs.audioUpload.clearFiles();
    }
  },

  beforeDestroy() {
    this.stopPolling()
  }
}
</script>

<style lang="scss" scoped>
.recognize-container {
  min-height: calc(100vh - 60px);
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.recognize-card {
  max-width: 1200px;
  width: 100%;
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px;
  text-align: center;
  color: white;

  h1 {
    margin: 0;
    font-size: 32px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    i {
      font-size: 36px;
    }
  }

  .subtitle {
    margin: 10px 0 0 0;
    font-size: 16px;
    opacity: 0.95;
  }
}

.upload-section {
  padding: 40px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 30px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

.upload-box {
  .upload-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    min-height: 32px;

    i {
      font-size: 28px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      line-height: 1;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #495057;
      line-height: 1.2;
    }

    .file-count {
      margin-left: auto;
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      color: white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }
  }

  ::v-deep .upload-area {
    .el-upload-dragger {
      width: 100%;
      height: 200px;
      border: 2px dashed #d9d9d9;
      border-radius: 12px;
      background: #fafafa;
      transition: all 0.3s ease;

      &:hover {
        border-color: #667eea;
        background: #f0f0ff;
      }

      .el-icon-upload {
        font-size: 67px;
        color: #c0c4cc;
        margin: 40px 0 16px;
        line-height: 50px;
      }

      .el-upload__text {
        color: #606266;
        font-size: 14px;
        line-height: 1.5;

        em {
          color: #667eea;
          font-style: normal;
          font-weight: 600;
        }
      }
    }

    .el-upload__tip {
      font-size: 12px;
      color: #909399;
      margin-top: 10px;
    }

    .el-upload-list {
      margin-top: 15px;
    }
  }
}

.vocals-section {
  padding: 0 40px 30px 40px;
  display: flex;
  justify-content: center;

  ::v-deep .el-checkbox {
    .el-checkbox__label {
      font-size: 16px;
      color: #495057;
      font-weight: 500;

      .checkbox-label {
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          font-size: 20px;
          color: #667eea;
        }
      }
    }

    &.is-checked .el-checkbox__inner {
      background-color: #667eea;
      border-color: #667eea;
    }

    &.is-checked .el-checkbox__label {
      color: #667eea;
    }
  }
}

.files-summary {
  margin: 0 40px 30px 40px;
  padding: 25px;
  background: linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e1e8ed;

  h3 {
    margin: 0 0 20px 0;
    font-size: 18px;
    color: #495057;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #667eea;
    }
  }

  .summary-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .summary-item {
    .summary-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #868e96;
      margin-bottom: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      i {
        color: #4facfe;
      }
    }

    .summary-files {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    .file-tag {
      background: white;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 8px 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #495057;
      transition: all 0.3s ease;

      &:hover {
        border-color: #667eea;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
      }

      i {
        color: #667eea;
      }

      .file-size {
        margin-left: 8px;
        color: #868e96;
        font-size: 11px;
      }
    }

    .summary-value {
      display: flex;
      gap: 10px;
    }
  }
}

.analysis-status {
  padding: 0 40px 30px 40px;

  .status-box {
    padding: 30px;
    border-radius: 12px;
    text-align: center;

    i {
      font-size: 48px;
      margin-bottom: 15px;
    }

    h3 {
      margin: 0 0 10px 0;
      font-size: 20px;
      font-weight: 600;
    }

    p {
      margin: 5px 0 0 0;
      font-size: 14px;
      opacity: 0.9;
    }

    .job-id {
      font-family: monospace;
      background: rgba(255, 255, 255, 0.3);
      padding: 2px 8px;
      border-radius: 4px;
      font-weight: 600;
    }

    .status-note {
      margin-top: 10px;
      font-size: 12px;
      opacity: 0.7;
    }

    &.uploading {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      color: white;

      i {
        animation: rotate 1s linear infinite;
      }
    }

    &.analyzing {
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
      color: white;

      i {
        animation: rotate 1s linear infinite;
      }
    }

    &.completed {
      background: linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%);
      color: white;

      i {
        animation: scaleIn 0.5s ease;
      }

      .view-results-button {
        margin-top: 15px;
        background: white;
        color: #84fab0;
        border: 2px solid white;
        font-weight: 600;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        i {
          margin-right: 6px;
          animation: none;
        }
      }
    }

    &.error {
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
      color: white;

      i {
        animation: shake 0.5s ease;
      }
    }
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-10px);
  }
  75% {
    transform: translateX(10px);
  }
}

.action-section {
  padding: 0 40px 40px 40px;
  display: flex;
  gap: 15px;
  justify-content: center;

  .submit-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 15px 40px;
    font-size: 16px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    i {
      margin-right: 8px;
    }
  }

  .reset-button {
    padding: 15px 30px;
    font-size: 16px;
    border-radius: 12px;
    border: 2px solid #667eea;
    color: #667eea;
    background: white;
    transition: all 0.3s ease;

    &:hover {
      background: #667eea;
      color: white;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
    }

    i {
      margin-right: 8px;
    }
  }
}

.tier-section {
  padding: 0 40px 30px 40px;
  display: flex;
  justify-content: center;

  .tier-selection-box {
    width: 100%;
    max-width: 600px;
    background: linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%);
    border: 2px solid #e1e8ed;
    border-radius: 12px;
    padding: 25px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
    text-align: center;

    .tier-label {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      font-size: 16px;
      color: #495057;
      font-weight: 600;
      width: 100%;

      i {
        font-size: 20px;
        color: #667eea;
      }
    }
    .tier-display {
      .tier-tag {
        font-size: 15px;
        font-weight: 600;
      }

      .no-tier-selected {
        color: #909399;
        font-style: italic;
        font-size: 14px;
      }
    }

    .select-tier-button {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
      }

      i {
        margin-right: 6px;
      }
    }
  }
}
</style>
